import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronsUpDown, Plus, Check, Building2, User, X, Shield, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Avatar, Button, Input, Chip } from "@/components/primitives";

function AddAccountModal({ onClose }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState("business");
  const [biz, setBiz] = useState({ name: "", tagline: "" });
  const [link, setLink] = useState({ email: "", password: "" });

  const createBiz = useMutation({
    mutationFn: () => api("/accounts/business", { method: "POST", body: biz }),
    onSuccess: async (a) => {
      qc.invalidateQueries({ queryKey: ["linked-accounts"] });
      onClose();
      if (a?.user_id) {
        try {
          const { url } = await api("/checkout", { method: "POST", body: { kind: "business", business_id: a.user_id } });
          if (url) { window.location.assign(url); return; }
        } catch (e) { toast.error(e.message || "Business created, add billing later"); }
      }
      toast.success("Business account created");
    },
    onError: (e) => toast.error(e.message),
  });
  const linkAcc = useMutation({
    mutationFn: () => api("/accounts/link", { method: "POST", body: link }),
    onSuccess: (a) => { toast.success(`Linked ${a.full_name}`); qc.invalidateQueries({ queryKey: ["linked-accounts"] }); onClose(); },
    onError: () => toast.error("Couldn't link, check the email and password"),
  });

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[60] bg-ink/40 flex items-end sm:items-center justify-center sm:p-6" onClick={onClose}>
      <div className="bg-surface-2 w-full sm:max-w-md rounded-t-3xl sm:rounded-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-ink">Add account</h2>
          <button onClick={onClose} className="text-muted-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-2 mb-5">
          <Chip label="Create business" active={mode === "business"} onClick={() => setMode("business")} testID="add-mode-business" />
          <Chip label="Link existing" active={mode === "link"} onClick={() => setMode("link")} testID="add-mode-link" />
        </div>

        {mode === "business" ? (
          <div className="flex flex-col gap-4">
            <Input label="Organization name" data-testid="biz-name" value={biz.name} onChange={(e) => setBiz({ ...biz, name: e.target.value })} placeholder="Mercer Studios" />
            <Input label="Tagline (optional)" data-testid="biz-tagline" value={biz.tagline} onChange={(e) => setBiz({ ...biz, tagline: e.target.value })} placeholder="Design partner for founders" />
            <p className="text-muted-foreground text-xs">A business account can host events & post jobs as your organization. You can switch to it any time.</p>
            <Button data-testid="biz-create" loading={createBiz.isPending} disabled={!biz.name.trim()} onClick={() => createBiz.mutate()}>Create business account</Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <Input label="Email" type="email" data-testid="link-email" value={link.email} onChange={(e) => setLink({ ...link, email: e.target.value })} placeholder="other@work.com" />
            <Input label="Password" type="password" data-testid="link-password" value={link.password} onChange={(e) => setLink({ ...link, password: e.target.value })} placeholder="Password for that account" />
            <p className="text-muted-foreground text-xs">Verify a personal account you also own to switch between them without signing in again.</p>
            <Button data-testid="link-submit" loading={linkAcc.isPending} disabled={!link.email.trim() || !link.password} onClick={() => linkAcc.mutate()}>Link account</Button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default function AccountSwitcher() {
  const { user, switchAccount } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const { data } = useQuery({ queryKey: ["linked-accounts"], queryFn: () => api("/accounts/linked") });

  const accounts = data?.accounts || [];
  const hasMulti = accounts.length > 1;

  const doSwitch = async (id) => {
    if (id === user.user_id) { setOpen(false); return; }
    try {
      const u = await switchAccount(id);
      qc.clear();
      setOpen(false);
      toast.success(`Switched to ${u.full_name}`);
      nav("/");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const unlink = useMutation({
    mutationFn: (id) => api("/accounts/unlink", { method: "POST", body: { user_id: id } }),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["linked-accounts"] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="relative">
      <button
        data-testid="account-switcher-trigger"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-surface-3 transition-colors"
      >
        <Avatar uri={user?.avatar_url} name={user?.full_name} size={40} />
        <div className="min-w-0 flex-1 text-left">
          <p className="font-serif text-ink text-[15px] truncate flex items-center gap-1">
            {user?.full_name}
            {user?.account_type === "business" && <Building2 className="w-3.5 h-3.5 text-brand" />}
          </p>
          <p className="text-muted-foreground text-xs truncate">
            {user?.account_type === "business" ? "Business" : user?.is_pro ? "Individual Plus member" : "Member"}
          </p>
        </div>
        <ChevronsUpDown className="w-4 h-4 text-muted-foreground shrink-0" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="account-switcher-menu" className="absolute bottom-full mb-2 left-0 right-0 z-50 bg-surface-2 border border-line rounded-lg shadow-xl p-1.5 animate-rise">
            <p className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">Switch account</p>
            {accounts.map((a) => (
              <div key={a.user_id} className="flex items-center gap-2 group">
                <button
                  data-testid={`switch-to-${a.user_id}`}
                  onClick={() => doSwitch(a.user_id)}
                  className="flex-1 flex items-center gap-3 px-2.5 py-2 rounded-md hover:bg-surface-3 transition-colors min-w-0"
                >
                  <Avatar uri={a.avatar_url} name={a.full_name} size={34} />
                  <div className="min-w-0 flex-1 text-left">
                    <p className="text-ink text-sm truncate flex items-center gap-1">
                      {a.full_name}
                      {a.account_type === "business" ? <Building2 className="w-3 h-3 text-brand" /> : <User className="w-3 h-3 text-muted-foreground" />}
                      {a.is_admin && <Shield className="w-3 h-3 text-brand" />}
                    </p>
                    <p className="text-muted-foreground text-xs truncate">{a.account_type === "business" ? "Business" : a.email}</p>
                  </div>
                  {a.user_id === user.user_id && <Check className="w-4 h-4 text-brand shrink-0" />}
                </button>
                {a.user_id !== user.user_id && (
                  <button data-testid={`unlink-${a.user_id}`} onClick={() => unlink.mutate(a.user_id)} className="p-2 rounded-md text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity" title="Remove">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            <div className="h-px bg-divider my-1.5" />
            <button data-testid="add-account-btn" onClick={() => { setOpen(false); setModal(true); }} className="w-full flex items-center gap-3 px-2.5 py-2 rounded-md hover:bg-surface-3 text-ink text-sm">
              <div className="w-[34px] h-[34px] rounded-full bg-surface-3 flex items-center justify-center"><Plus className="w-4 h-4" /></div>
              Add or create account
            </button>
          </div>
        </>
      )}

      {modal && <AddAccountModal onClose={() => setModal(false)} />}
      {hasMulti && null}
    </div>
  );
}
